import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Ruler, X } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import {
  getGLBUrl,
  getThicknessGLBUrl,
} from '../../services/cad.service';
import {
  CADMarker,
  CADModel,
  ThicknessAnalysis,
} from '../../types/cad';
import CADModelList from './CADModelList';
import CADModelViewer from './CADModelViewer';
import CADThicknessPanel, {
  ThicknessViewState,
} from './CADThicknessPanel';
import CADThicknessUploadQueue from './CADThicknessUploadQueue';
import CadViewerModeButton from './CadViewerModeButton';

interface CADThicknessWorkspaceProps {
  models: CADModel[];
  selectedModel: CADModel | null;
  canAnalyze: boolean;
  canDelete: boolean;
  error: string | null;
  onClearError: () => void;
  onSelectModel: (model: CADModel) => void;
  onUploadComplete: (model: CADModel) => void;
  onDeleteModel: (modelId: string) => void;
  onEditClassification?: (model: CADModel) => void;
  onExitThicknessMode?: () => void;
}

const CADThicknessWorkspace: React.FC<CADThicknessWorkspaceProps> = ({
  models,
  selectedModel,
  canAnalyze,
  canDelete,
  error,
  onClearError,
  onSelectModel,
  onUploadComplete,
  onDeleteModel,
  onEditClassification,
  onExitThicknessMode,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [viewState, setViewState] = useState<ThicknessViewState>({
    analysis: null,
    region: null,
    displayMode: 'smooth',
  });

  // URL writes commit in a router transition, each with a new location.key: until the key moves
  // past our last write the URL is stale, and ?analysis= / ?region= apply only to the selected model.
  const location = useLocation();
  const [writeBaseKey, setWriteBaseKey] = useState<string | null>(null);
  const pendingWriteRef = useRef<{ baseKey: string; params: URLSearchParams } | null>(null);
  const urlWritePending = writeBaseKey === location.key;
  const urlNamesSelectedModel = !urlWritePending
    && searchParams.get('model') === selectedModel?.id;
  const requestedAnalysisId = urlNamesSelectedModel ? searchParams.get('analysis') : null;
  const requestedRegionId = urlNamesSelectedModel ? searchParams.get('region') : null;

  const updateParams = useCallback((
    updates: Partial<Record<'model' | 'analysis' | 'region', string | null>>,
  ) => {
    setWriteBaseKey(location.key);
    setSearchParams((current) => {
      // The router hands us the rendered params; a write made before our previous
      // one commits must build on that write, not undo it.
      const pending = pendingWriteRef.current;
      const next = new URLSearchParams(pending?.baseKey === location.key ? pending.params : current);
      // A run and region only mean something for the model they were written with.
      if ('model' in updates && updates.model !== next.get('model')) {
        next.delete('analysis');
        next.delete('region');
      }
      // Re-affirming the run the URL already names keeps its region.
      const sameRun = updates.analysis != null && updates.analysis === next.get('analysis');
      next.set('mode', 'thickness');
      Object.entries(updates).forEach(([key, value]) => {
        if (key === 'region' && value === null && sameRun) return;
        if (value) next.set(key, value);
        else next.delete(key);
      });
      pendingWriteRef.current = { baseKey: location.key, params: next };
      return next;
    }, { replace: true });
  }, [location.key, setSearchParams]);

  const handleSelectModel = useCallback((model: CADModel) => {
    if (model.id === selectedModel?.id) return;
    onSelectModel(model);
    setViewState({ analysis: null, region: null, displayMode: 'smooth' });
    updateParams({
      model: model.id,
      analysis: null,
      region: null,
    });
  }, [onSelectModel, selectedModel?.id, updateParams]);

  const handlePipelineComplete = useCallback((
    model: CADModel,
    analysis: ThicknessAnalysis,
  ) => {
    // Keep the selected model, displayed analysis, and URL in one React batch.
    // With two concurrent uploads, another pipeline may have selected its
    // model between this pipeline's conversion and analysis completion.
    // Updating only the URL briefly paired that other model with this run,
    // causing authenticated review/GLB requests to return 404.
    onUploadComplete(model);
    setViewState({
      analysis,
      region: null,
      displayMode: 'flat',
    });
    setRefreshKey((current) => current + 1);
    updateParams({
      model: model.id,
      analysis: analysis.analysisId,
      region: null,
    });
  }, [onUploadComplete, updateParams]);

  const handleModelAvailable = useCallback((model: CADModel) => {
    onUploadComplete(model);
    updateParams({
      model: model.id,
      analysis: null,
      region: null,
    });
  }, [onUploadComplete, updateParams]);

  const handleViewChange = useCallback((next: ThicknessViewState) => {
    setViewState(next);
  }, []);

  const flatAnalysisId =
    viewState.displayMode === 'flat' &&
    viewState.analysis?.status === 'completed' &&
    viewState.analysis.modelId === selectedModel?.id
      ? viewState.analysis.analysisId
      : null;
  const glbUrl = selectedModel?.status === 'completed'
    ? flatAnalysisId
      ? getThicknessGLBUrl(selectedModel.id, flatAnalysisId)
      : getGLBUrl(selectedModel.id)
    : null;

  const regionMarkers = useMemo<CADMarker[]>(() => {
    if (!viewState.region) return [];
    const region = viewState.region;
    const extreme = region.judgment === 'thin' ? region.minMm : region.maxMm;
    return [{
      position: region.extremePoint,
      label: `${region.regionId}: ${extreme.toFixed(2)} mm`,
      type: region.judgment === 'thin' ? 'error' : 'warning',
    }];
  }, [viewState.region]);

  return (
    <div
      data-testid="thickness-workspace"
      className="flex min-h-0 flex-1 flex-col gap-1.5 p-2 sm:p-4"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <Ruler className="mt-0.5 size-6 shrink-0 text-primary" />
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h1 className="whitespace-nowrap text-xl font-bold text-foreground sm:text-2xl">
              {t('cad.thickness.title')}
            </h1>
            <p className="text-sm leading-tight text-muted-foreground">
              {t('cad.thickness.subtitle')}
            </p>
          </div>
        </div>
        {onExitThicknessMode && (
          <div className="shrink-0 self-start">
            <CadViewerModeButton
              thicknessMode
              onToggle={onExitThicknessMode}
              disabled={pipelineRunning}
            />
          </div>
        )}
      </div>

      {/* Compact like main's `sx={{ py: 0 }}`, but MUI kept padding on its inner
          message element and shadcn does not — py-0 leaves the text on the border. */}
      <Alert variant="info" className="py-2">
        {t('cad.thickness.scopeNotice')}
      </Alert>
      {error && (
        <Alert variant="destructive" className="flex items-start justify-between gap-2">
          <span>{error}</span>
          <TooltipIconButton
            label={t('common.close')}
            icon={<X className="size-4" />}
            size="icon"
            className="-my-1 -mr-1 size-6 shrink-0 text-destructive"
            onClick={onClearError}
          />
        </Alert>
      )}

      <div
        data-testid="thickness-panel-layout"
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto overscroll-contain lg:flex-row lg:overflow-hidden"
      >
        <div
          data-testid="thickness-navigation-panel"
          className="flex w-full shrink-0 flex-col overflow-visible rounded-lg border border-border bg-card lg:h-auto lg:w-80 lg:overflow-hidden"
        >
          <div
            role="region"
            aria-label={t('cad.thickness.batch.regionLabel')}
            tabIndex={0}
            className="min-h-0 shrink-0 overflow-visible overscroll-contain focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring lg:max-h-[60%] lg:overflow-y-auto"
            data-testid="thickness-batch-scroll-region"
          >
            <CADThicknessUploadQueue
              canAnalyze={canAnalyze}
              onModelAvailable={handleModelAvailable}
              onPipelineComplete={handlePipelineComplete}
              onRunningChange={setPipelineRunning}
            />
          </div>
          <Separator />
          <div className="px-3 pb-1.5 pt-3">
            <p className="text-xs font-bold text-muted-foreground">
              {t('cad.uploadedModels', { count: models.length })}
            </p>
          </div>
          <div
            data-testid="thickness-model-list-region"
            className="overflow-visible px-2 pb-2 lg:min-h-0 lg:flex-1 lg:overflow-auto"
          >
            <CADModelList
              models={models}
              selectedModelId={selectedModel?.id || null}
              onSelect={handleSelectModel}
              onDelete={canDelete ? onDeleteModel : undefined}
              onEditClassification={onEditClassification}
            />
          </div>
        </div>

        <div className="h-[420px] min-h-[320px] w-full shrink-0 overflow-hidden rounded-lg border border-border bg-card lg:h-auto lg:min-h-0 lg:flex-1">
          <CADModelViewer
            glbUrl={glbUrl}
            filename={selectedModel?.filename}
            markers={regionMarkers}
            preserveMaterialColors={Boolean(flatAnalysisId)}
            thicknessPresentation
            focusPoint={viewState.region?.extremePoint || null}
          />
        </div>

        <div className="w-full shrink-0 overflow-visible rounded-lg border border-border bg-card lg:w-[410px] lg:overflow-auto">
          <CADThicknessPanel
            model={selectedModel}
            canAnalyze={canAnalyze}
            canApprove={canDelete}
            requestedAnalysisId={requestedAnalysisId}
            requestedRegionId={requestedRegionId}
            refreshKey={refreshKey}
            seedAnalysis={viewState.analysis}
            urlWritePending={urlWritePending}
            onAnalysisChange={(analysisId) =>
              updateParams({ model: selectedModel?.id ?? null, analysis: analysisId, region: null })
            }
            onRegionChange={(regionId) =>
              updateParams({ model: selectedModel?.id ?? null, region: regionId })
            }
            onViewChange={handleViewChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CADThicknessWorkspace;
